(ns manage.adset-decision-tree-page.editor.core
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/AlertTitle$default" :as AlertTitle]
            ["@mui/material/Snackbar$default" :as Snackbar]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/material/styles" :as styles]
            ["@xyflow/react"
             :refer [Background
                     ControlButton
                     Controls
                     MiniMap
                     Panel
                     ReactFlow
                     useReactFlow]]
            ["react" :as react]
            ["react-use/lib/useKey$default" :as useKey]
            ["src/svg/inform/error-triangle-fill.svg$default" :as AlertIcon]
            ["src/svg/path/tree-vertical.svg$default" :as TreeVerticalIcon]
            [cljs-bean.core :refer [->clj ->js]]
            [clojure.edn :as edn]
            [common.flags :refer [use-flag]]
            [common.modal.dialog :refer [deletion-confirmation]]
            [common.modal.view :refer [defmodal]]
            [common.resource.query.social-settings :as social-settings]
            [goog.object :as gobject]
            [manage.ad-variant.optimization-ocp.view :refer [delivery-optimization-box]]
            [manage.adset-decision-tree-page.editor.edge
             :refer [edge use-default-edge-props use-update-edge-color-by-selection]]
            [manage.adset-decision-tree-page.editor.hooks :refer [use-fit-view]]
            [manage.adset-decision-tree-page.editor.node
             :refer [decision-tree-decision-node*]]
            [manage.adset-decision-tree-page.editor.path-panel :refer [path-panel]]
            [manage.adset-decision-tree-page.editor.root-node
             :refer [decision-tree-root-node]]
            [manage.adset-decision-tree-page.event :as event]
            [manage.adset-decision-tree-page.node-library :as node-library]
            [manage.adset-decision-tree-page.rotation :as rotation]
            [manage.adset-decision-tree-page.selection :as selection]
            [manage.adset-decision-tree-page.sub :as sub]
            [manage.adset-decision-tree-page.view.constant
             :refer [palette-node-mime-type]]
            [manage.adset-decision-tree-page.view.header
             :refer [upgrade-all-variants-button
                     remove-archived-ad-variants-button]]
            [manage.adset-decision-tree-page.view.hook :as hook]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defmodal confirm-delete :decision-tree/confirm-node-delete [{:keys [entity-name on-submit]}]
  [deletion-confirmation
   {:entity-name entity-name
    :confirmation-message "This will also delete the Outcomes. Would you like to proceed?"
    :submit-button-label "Proceed and Delete"
    :on-submit #(rf/dispatch on-submit)}])

(def read-only-props
  {:nodesDraggable false
   :nodesConnectable false})

(defn on-before-delete [node-id->node-data {:keys [nodes] :as items-to-delete}]
  (let [node-ids-to-delete (set (map :id nodes))
        ;; Remove edges connected to nodes to be deleted,
        ;; because those will be handled implicitly
        items-to-delete (update items-to-delete :edges
                                (fn [edges]
                                  (vec
                                   (remove (fn [{:keys [source target]}]
                                             (or (contains? node-ids-to-delete source)
                                                 (contains? node-ids-to-delete target)))
                                           edges))))
        node-data-coll (map (comp node-id->node-data parse-long) node-ids-to-delete)
        outcome-node-ids-to-be-removed-implicitly (->> node-data-coll
                                                       (mapcat :outcome-node-ids))
        entity-name (if (= (count nodes) 1)
                      (str "this "
                           (-> (first node-data-coll)
                               :type
                               node-library/type->node
                               :name)
                           " Decision node")
                      "these Decision nodes")]
    (if (seq outcome-node-ids-to-be-removed-implicitly)
      (js/Promise.
       (fn [resolve _reject]
         (rf/dispatch
          [:modal/open
           {:id :decision-tree/confirm-node-delete
            :props {:entity-name entity-name
                    :on-submit [::event/resolve-delete-confirm
                                #(resolve (->js items-to-delete))]}
            :on-close [::event/resolve-delete-confirm #(resolve false)]}])))
      (->js items-to-delete))))

(defn connection-introduces-cycle? [node-id->node-data source-id target-id]
  (->> (iterate (fn [node-id] (get-in node-id->node-data [node-id :parent-id])) source-id)
       (some (fn [node-id]
               (cond
                 (= node-id target-id) :new-cycle-found
                 (nil? node-id) :no-new-cycle-found)))
       (= :new-cycle-found)))

(defn- rotation-connection-blocked? [node-id->node-data source source-handle]
  (rotation/connect-blocked-for-outcome?
   (parse-long (or source-handle source))
   node-id->node-data))

(defn- find-sibling
  [{:keys [node-id->node-data node-id->children]} node direction]
  (when node
    (let [;; To find a sibling, we always navigate based on the position of an outcome-node.
          ;; If node is an outcome-node, we use it directly.
          ;; If node is a decision-node, we first go up to its parent,
          ;; since the decision-node is a child of an outcome-node.
          outcome-node? (= (:category node) :outcome-node)

          closest-outcome (if outcome-node?
                            node
                            (-> (:parent-id node) node-id->node-data))

          outcome-node-ids (some-> closest-outcome
                                   :parent-id
                                   node-id->node-data
                                   :outcome-node-ids)

          closest-outcome-idx (some-> outcome-node-ids
                                      (.indexOf (:id closest-outcome)))

          left-sibling-outcome-node-ids (some-> outcome-node-ids
                                                (subvec 0 closest-outcome-idx)
                                                reverse)

          right-sibling-outcome-node-ids (some-> outcome-node-ids
                                                 (subvec (inc closest-outcome-idx)))

          sibling-outcome-node-ids (case direction
                                     :left left-sibling-outcome-node-ids
                                     :right right-sibling-outcome-node-ids)]

      ;; For outcome nodes, return the first sibling outcome directly.
      ;; For decision nodes, return first child of sibling outcome (if present).
      (if outcome-node?
        (-> sibling-outcome-node-ids
            first
            node-id->node-data)
        (some (comp first node-id->children)
              sibling-outcome-node-ids)))))

(defhook use-keyboard-navigation []
  (let [fit-view (use-fit-view)
        node-id->node-data (use-subscribe [::sub/node-id->node-data])
        node-id->children (use-subscribe [::sub/node-id->children])
        selected-item (use-subscribe [::sub/first-selected-item])
        selected-node (use-subscribe [::sub/selected-node selected-item])

        {default-outcome-node-active :active}
        (hook/use-default-outcome-node-control {:id (:decision-node-id selected-item)})

        find-sibling (uix/use-callback
                      (fn [selected-node direction]
                        (find-sibling {:node-id->node-data node-id->node-data
                                       :node-id->children node-id->children}
                                      selected-node
                                      direction))
                      [node-id->node-data node-id->children])

        children (uix/use-memo
                  (fn []
                    (some->> (:id selected-node)
                             node-id->children
                             (sort-by :ordinal)))
                  [node-id->children selected-node])

        parent (uix/use-memo
                (fn []
                  (let [parent-id (if (= (:type selected-item) :selection/default-outcome-node)
                                    (:decision-node-id selected-item)
                                    (:parent-id selected-node))]
                    (some-> parent-id node-id->node-data)))
                [node-id->node-data selected-node selected-item])

        first-child (uix/use-memo
                     (fn []
                       ;; default-outcome-node doesn't have children,
                       ;; consider default-outcome-node as child for decision-node.
                       (case (:type selected-item)
                         :selection/default-outcome-node nil
                         :selection/decision-node (or (first children)
                                                      (and default-outcome-node-active
                                                           {:category :default-outcome-node
                                                            :id (:decision-node-id selected-item)}))
                         (first children)))
                     [selected-item children default-outcome-node-active])

        left-sibling (uix/use-memo
                      (fn []
                        ;; We consider the last child of the decision-node
                        ;; the left sibling of its default-outcome-node.
                        (if (= (:type selected-item) :selection/default-outcome-node)
                          (last children)
                          (find-sibling selected-node :left)))
                      [find-sibling selected-node selected-item children])

        right-sibling (uix/use-memo
                       (fn []
                         ;; default-outcome-node doesn't have siblings to its right.
                         ;; consider default-outcome-node as right sibling for outcome-node.
                         (case (:type selected-item)
                           :selection/default-outcome-node nil
                           :selection/outcome-node (or (find-sibling selected-node :right)
                                                       (and default-outcome-node-active
                                                            {:category :default-outcome-node
                                                             :id (:decision-node-id selected-item)}))
                           (find-sibling selected-node :right)))
                       [find-sibling selected-node selected-item default-outcome-node-active])

        select-node (uix/use-callback
                     (fn [node-to-select]
                       (when node-to-select
                         (let [selection-item (selection/get-selected-item-for-node node-to-select)]
                           (rf/dispatch [::event/set-selection [selection-item]])
                           (fit-view [(str (:decision-node-id selection-item))]))))
                     [fit-view])

        handle-navigation (uix/use-callback
                           (fn [^js e node-to-select]
                             (let [target (.-target e)
                                   tag-name (.-tagName target)
                                   is-input (or (contains? #{"INPUT" "SELECT"} tag-name)
                                                (.closest target ".MuiPickersPopper-root"))]
                               (when-not is-input
                                 (select-node node-to-select))))
                           [select-node])]

    (useKey "ArrowLeft" #(handle-navigation % parent)
            #js {}
            #js [parent])

    (useKey "ArrowRight" #(handle-navigation % first-child)
            #js {}
            #js [first-child])

    (useKey "ArrowUp" #(handle-navigation % left-sibling)
            #js {}
            #js [left-sibling])

    (useKey "ArrowDown" #(handle-navigation % right-sibling)
            #js {}
            #js [right-sibling])))

(defhook use-reactflow-interaction-props []
  (let [reactflow-instance (useReactFlow)
        read-only (use-subscribe [::sub/editor-read-only])
        on-nodes-change (react/useCallback (fn [^js changes]
                                             ;; (js/console.log "on-nodes-change" changes)
                                             (rf/dispatch-sync [::event/on-nodes-change changes]))
                                           #js [])
        on-edges-change (react/useCallback (fn [^js changes]
                                             ;; (js/console.log "on-edges-change" changes)
                                             (rf/dispatch-sync [::event/on-edges-change changes]))
                                           #js [])
        on-connect (react/useCallback (fn [^js params]
                                        ;; (js/console.log "on-connect" params)
                                        (rf/dispatch-sync [::event/on-connect params]))
                                      #js [])

        on-drag-over (react/useCallback
                      (fn [^js event]
                        (let [dragged-mime-types (set (.. event -dataTransfer -types))]
                          (when (contains? dragged-mime-types palette-node-mime-type)
                            ;; Preventing the default means that we accept dropping the item here.
                            (.preventDefault event))))
                      #js [])

        on-drop (react/useCallback
                 (fn [^js event]
                   ;; The browser's default behavior is "open as link".
                   (.preventDefault event)

                   (let [node-type (-> (.. event -dataTransfer (getData palette-node-mime-type))
                                       edn/read-string)
                         node (-> node-type node-library/type->node)
                         position (.screenToFlowPosition reactflow-instance
                                                         #js {:x (.-clientX event)
                                                              :y (.-clientY event)})]
                     (rf/dispatch [::event/be-create-node {:type node-type
                                                           :label (:label node "")
                                                           :visualization {:x (.-x position)
                                                                           :y (.-y position)}}])))
                 #js [reactflow-instance])

        node-id->node-data (use-subscribe [::sub/node-id->node-data])
        on-before-delete (uix/use-callback
                          (fn [^js o]
                            (if read-only
                              false
                              (on-before-delete node-id->node-data (->clj o))))
                          [read-only node-id->node-data])

        is-valid-connection (uix/use-callback
                             (fn [^js connection]
                               (let [{:keys [source target sourceHandle]} (->clj connection)]
                                 (and (not (connection-introduces-cycle? node-id->node-data
                                                                         (parse-long source)
                                                                         (parse-long target)))
                                      (not (rotation-connection-blocked? node-id->node-data
                                                                         source
                                                                         sourceHandle)))))
                             [node-id->node-data])]
    {:onNodesChange on-nodes-change
     :onEdgesChange on-edges-change
     :onConnect on-connect
     :isValidConnection is-valid-connection
     :onDragOver on-drag-over
     :onDrop on-drop
     :onPaneClick #(rf/dispatch [::event/set-selection nil])
     :onBeforeDelete on-before-delete
     :disableKeyboardA11y true}))

(def reactflow-default-props
  {:fitViewOptions #js {:maxZoom 1}
   :fitView true
   ;; https://reactflow.dev/learn/troubleshooting/remove-attribution
   :proOptions #js {:hideAttribution true}
   :onlyRenderVisibleElements true
   :nodeTypes #js {"decision-tree-root-node"     (react/memo decision-tree-root-node)
                   "decision-tree-decision-node" (react/memo decision-tree-decision-node*)}
   :edgeTypes #js {"default" (react/memo edge)}})

(defhook use-reactflow-default-props []
  (let [edge-fn (use-update-edge-color-by-selection)
        default-edge-props (use-default-edge-props)
        nodes (use-subscribe [::sub/reactflow-nodes])
        edges (use-subscribe [::sub/reactflow-edges-for-render {:edge-fn edge-fn}])]
    (merge {:nodes nodes
            :edges edges}
           reactflow-default-props
           default-edge-props)))

;; Debugging information
(defui selection-info-overlay []
  (let [selection (use-subscribe [::sub/selection])]
    ($ :pre
       ($ :code
          (with-out-str (cljs.pprint/pprint selection))))))

(defhook use-auto-layout []
  (let [auto-layout? (use-subscribe [::sub/editor-auto-layout])
        reactflow-nodes (use-subscribe [::sub/reactflow-nodes])
        reactflow-edges (use-subscribe [::sub/reactflow-edges])
        node-id->node-data (use-subscribe [::sub/node-id->node-data])
        ordered-outcome-ids (uix/use-memo #(->> (vals node-id->node-data)
                                                (mapcat :outcome-node-ids))
                                          [node-id->node-data])]
    (uix/use-effect #(when auto-layout?
                       (rf/dispatch [::event/re-layout-nodes]))
                    [auto-layout?
                     (count reactflow-nodes)
                     (count reactflow-edges)
                     (count (filter #(.-hidden %) reactflow-nodes))
                     ordered-outcome-ids])))

(defui ^:memo re-layout-control-button []
  (let [theme (styles/useTheme)
        auto-layout? (use-subscribe [::sub/editor-auto-layout])]
    ($ ControlButton
       {:onClick (fn [^js _event]
                   (rf/dispatch [::event/toggle-auto-layout]))
        :style #js {:backgroundColor (when auto-layout?
                                       (.. theme -palette -primary -selected))}}
       ($ Tooltip
          {:title "Auto layout: Automatically arrange cards for a cleaner, organized canvas"
           :placement "left"}
          ($ SvgIcon
             {:sx #js {:maxHeight "unset!important"
                       :maxWidth "unset!important"
                       :color (when auto-layout?
                                "primary.main")}}
             ($ TreeVerticalIcon))))))

(defui re-layout-control []
  (let [read-only (use-subscribe [::sub/editor-read-only])]
    (use-auto-layout)
    (when-not read-only
      ($ re-layout-control-button))))

(defui decision-tree-snackbar [{:keys [open alert-props title children]}]
  (let [[snackbar-open set-snackbar-open] (uix/use-state false)]
    (uix/use-effect #(when open (set-snackbar-open true)) [open])
    ($ Snackbar {:open (boolean (and snackbar-open open))
                 :sx #js {:position "relative"}
                 :anchorOrigin #js {:vertical "top" :horizontal "center"}}
       ($ Alert (merge {:onClose #(set-snackbar-open false)
                        :severity "info"
                        :elevation 3
                        :sx #js {:width "calc(100% - 48px)"}}
                       alert-props)
          ($ AlertTitle title)
          children))))

(defui upgrade-all-variants-snackbar []
  (let [has-newer-revisions (use-subscribe [::sub/has-newer-revisions])
        read-only (use-subscribe [::sub/editor-read-only])]
    ($ decision-tree-snackbar {:open (and has-newer-revisions (not read-only))
                               :title "You have new versions available to be updated"}
       ($ upgrade-all-variants-button {:variant "contained"
                                       :sx #js {:mt 1}}))))

(defui remove-archived-ad-variants-snackbar []
  (let [has-archived (use-subscribe [::sub/has-archived-ad-variants-in-tree])
        read-only (use-subscribe [::sub/editor-read-only])]
    ($ decision-tree-snackbar {:open (and has-archived (not read-only))
                               :title "This decision tree contains archived ad variants"
                               :alert-props {:severity "warning"}}
       ($ remove-archived-ad-variants-button {:sx #js {:mt 1}}))))

(defui automation-warning-snackbar []
  (let [read-only (use-subscribe [::sub/editor-read-only])
        auto-publish? (social-settings/use-auto-publish)]
    ($ decision-tree-snackbar {:open (and auto-publish? (not read-only))
                               :title "Automation is paused"
                               :alert-props {:severity "warning"
                                             :icon ($ AlertIcon)}}
       ($ Typography "Automation is paused while you edit this Decision Tree and will resume once the draft is published or discarded."))))

(defui ^:memo reactflow-children []
  ($ :<>
     ($ path-panel)
     ($ Panel {:position "top-right"}
        ($ Stack {:alignItems "end"}
           #_($ selection-info-overlay)))
     ($ Background {:size 5
                    :gap 35
                    :color (-> (styles/useTheme) .-palette .-grey (gobject/get 70))})
     ($ Controls {:showInteractive false}
        ($ re-layout-control))
     ($ MiniMap {:zoomable true
                 :pannable true})
     ($ upgrade-all-variants-snackbar)
     ($ remove-archived-ad-variants-snackbar)
     ($ automation-warning-snackbar)))

(defui read-only-reactflow []
  (use-keyboard-navigation)
  ($ ReactFlow (-> (use-reactflow-default-props)
                   (into (use-reactflow-interaction-props))
                   (into read-only-props)
                   ->js)
     ($ reactflow-children)))

(defui interactive-reactflow []
  (use-keyboard-navigation)
  ($ ReactFlow (-> (use-reactflow-default-props)
                   (into (use-reactflow-interaction-props))
                   ->js)
     ($ reactflow-children)))

(defui graph-view []
  (let [read-only (use-subscribe [::sub/editor-read-only])
        editor-is-initialized (use-subscribe [::sub/editor-is-initialized])
        aco-enabled? (use-flag :ad-variant-content-optimizing)]
    ($ Stack {:sx #js {:width "100%"
                       :height "100%"
                       :backgroundColor "grey.80"
                       ;; This is making the edge selection easier, has no visual impact
                       "& path.react-flow__edge-path:hover" #js {:strokeWidth 8}
                       ;; Animation for re-layout
                       "& .react-flow__node:not(.dragging)" #js {:transition "transform 0.2s"}}}
       (when (and aco-enabled? (not read-only))
         ($ delivery-optimization-box
            {:sx #js {:position "absolute"
                      :top "16px"
                      :left "16px"
                      :right "16px"
                      :zIndex 1}}))
       (when editor-is-initialized
         (if read-only
           ($ read-only-reactflow)
           ($ interactive-reactflow))))))

(defui root-view []
  ($ graph-view))
